import { Button, CloseIcon, LanguageIcon, MenuIcon, ToolButtonProps } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router'
import styled, { css } from 'styled-components'

import { StoreContext } from '../../store'
import { breakpoint } from '../../utils'
import CookieLink from '../general/CookieLink'
import { LangSelectButton } from '../lang/LangSelectButton'
import { ServiceList } from '../ServiceList'
import { getExampleSideMenuStructure, SideMenuPropsMainItem } from '../SideNav/Structure'

const MobileNavComponent: React.FC<RouteComponentProps & { setMobileMenuOpen?: Dispatch<SetStateAction<boolean>> }> = ({
  history,
  location,
  setMobileMenuOpen,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const {
    infoStore,
    mainViewStore,
    userStore,
    layoutStore: { isAppWebView },
  } = useContext(StoreContext)
  const isConsumer = infoStore.isConsumer
  const hasOrgs = userStore.hasOrganizations
  const isStrongAuth = infoStore.isStrongAuth
  const structure = getExampleSideMenuStructure(
    history,
    location,
    t,
    isConsumer,
    hasOrgs,
    isStrongAuth,
    mainViewStore.reset
  ).filter((item) => !item.mobileOnly)

  useEffect(() => {
    if (!isOpen) {
      buttonRef.current?.focus()
    } else {
      closeButtonRef.current?.focus()
    }
  }, [isOpen])

  const handleOutsideClick = useCallback(() => {
    setIsOpen(false)
    setMobileMenuOpen?.(false)
  }, [setMobileMenuOpen])

  const toggleOpen: ToolButtonProps['onClick'] = useCallback(
    (e) => {
      setIsOpen((open) => {
        setMobileMenuOpen?.(!open)
        return !open
      })
      e.stopPropagation()
    },
    [setMobileMenuOpen]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        handleOutsideClick()
      }
    }

    document.addEventListener('mousedown', onDocumentMouseDown)
    return () => document.removeEventListener('mousedown', onDocumentMouseDown)
  }, [handleOutsideClick, isOpen])

  return (
    <div ref={rootRef}>
      <Button
        leftIcon={!isOpen ? MenuIcon : CloseIcon}
        contentMode="tertiary"
        isOnlyIcon
        hasBackground
        ref={buttonRef}
        onClick={toggleOpen}
        aria-label={isOpen ? t('general.close') : t('common.menu')}
      />

      {isOpen && (
        <StyledMobileMenu $isAppWebView={isAppWebView}>
          {!isAppWebView && (
            <HeaderButtons>
              <Button
                leftIcon={CloseIcon}
                ref={closeButtonRef}
                onClick={toggleOpen}
                aria-label={t('general.close')}
                isOnlyIcon
                contentMode="tertiary"
                hasBackground
                size="xs"
              />
            </HeaderButtons>
          )}
          <ModalContent>
            <ContentGroup>
              {structure.map((item: SideMenuPropsMainItem) => (
                <StyledMobileMenuItem
                  aria-label={item.label}
                  id={item.route}
                  key={item.route}
                  value={item.route}
                  type="button"
                  onClick={() => {
                    item.onClick(item.route)
                    handleOutsideClick()
                  }}
                  $selected={item.isActive(item.route)}
                >
                  <item.icon aria-hidden={true} width="1.5rem" height="1.5rem" />
                  <span>{item.label}</span>
                </StyledMobileMenuItem>
              ))}
              <ServiceList />
            </ContentGroup>
            <ContentGroup>
              {isAppWebView && (
                <div style={{ padding: '20px 0 0 20px' }}>
                  <CookieLink idString="side_nav_cookie_link" color="black" />
                </div>
              )}
              <LanguageSelection aria-label={t(`common.languageSelection`)}>
                <LanguageLabel>
                  <LanguageIcon aria-hidden width="1.5rem" height="1.5rem" />
                  <span>{t('detailsView.language')}</span>
                </LanguageLabel>
                <LangSelectButton isLast isInline />
              </LanguageSelection>
            </ContentGroup>
          </ModalContent>
        </StyledMobileMenu>
      )}
    </div>
  )
}

export const MobileNav = withRouter(observer(MobileNavComponent))

export const StyledMobileMenu = styled.aside<{ $isAppWebView?: boolean }>(
  ({ theme: { colorTokens, spacing }, $isAppWebView = false }) => {
    return css`
      position: fixed;
      top: ${spacing.space18}rem;
      right: 0;
      z-index: 100;
      width: 100%;
      max-width: 24rem;
      height: calc(100dvh - ${spacing.space18}rem);
      overflow: hidden;
      color: ${colorTokens.black};
      background: ${colorTokens.white};
      box-shadow: 0 8px 22px rgba(27, 31, 35, 0.07);
      flex-direction: column;
      display: flex;

      @media ${breakpoint.mobile} {
        top: 0;
        height: 100dvh;
        max-height: none;
      }

      ${$isAppWebView &&
      css`
        @media ${breakpoint.mobile} {
          top: ${spacing.space26}rem;
          left: 0;
          height: calc(100dvh - ${spacing.space26}rem);
          max-height: calc(100dvh - ${spacing.space26}rem);
        }
      `}
    `
  }
)

const StyledMobileMenuItem = styled.button<{ $selected: boolean }>(
  ({ $selected, theme: { colorTokens, spacing } }) => css`
    display: flex;
    gap: ${spacing.space3}rem;
    align-items: center;
    width: 100%;
    padding: ${spacing.space4}rem ${spacing.space5}rem;
    color: inherit;
    text-align: left;
    background: ${$selected ? colorTokens.gray20 : 'transparent'};
    border: 0;
    cursor: pointer;

    &:focus {
      outline: 2px solid ${colorTokens.postiOrange60};
      outline-offset: -2px;
    }
  `
)

const ModalContent = styled.div(
  () => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    min-height: 0;
    overflow-y: auto;
  `
)

const HeaderButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.space3}rem;
`

const ContentGroup = styled.div`
  padding: ${({ theme }) => theme.spacing.space3}rem 0;
`

const LanguageSelection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.space4}rem;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.space6}rem ${({ theme }) => theme.spacing.space4}rem;
  border-top: 1px solid ${({ theme }) => theme.colorTokens.gray20};
`

const LanguageLabel = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.space3}rem;
  align-items: center;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`
